import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LogIn, LayoutDashboard, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_health-ghost/artifacts/h0ankv1n_logo.png";

const productItems = [
  { name: "Sva — Detox", path: "/products/sva-detox", desc: "Phone addiction & screen time" },
];

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    setLoggedIn(!!localStorage.getItem('user'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => location.pathname === path;
  const isProductActive = location.pathname.startsWith("/products");

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center group" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="BastionGuard Medical Technologies" className="h-12 md:h-14 w-auto group-hover:opacity-90 transition-opacity" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={`nav-link ${isActive('/') ? 'nav-link-active' : ''}`} data-testid="nav-link-home">Home</Link>
            <Link to="/about" className={`nav-link ${isActive('/about') ? 'nav-link-active' : ''}`} data-testid="nav-link-about">About</Link>

            {/* Products Dropdown */}
            <div className="relative" ref={dropdownRef} data-testid="nav-products-dropdown">
              <button
                onClick={() => setProductsOpen(!productsOpen)}
                onMouseEnter={() => setProductsOpen(true)}
                className={`nav-link flex items-center gap-1 ${isProductActive ? 'nav-link-active' : ''}`}
                data-testid="nav-link-products"
              >
                Products <ChevronDown size={14} className={`transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
              </button>
              {productsOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-64 rounded-xl border border-slate-700/60 bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                  onMouseLeave={() => setProductsOpen(false)}
                  data-testid="products-dropdown-menu"
                >
                  {productItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setProductsOpen(false)}
                      className="flex flex-col px-4 py-3 hover:bg-slate-800/80 transition-colors"
                      data-testid={`dropdown-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <span className="text-sm font-medium text-white">{item.name}</span>
                      <span className="text-xs text-slate-500">{item.desc}</span>
                    </Link>
                  ))}
                  <div className="border-t border-slate-800/60 px-4 py-2">
                    <span className="text-[11px] text-slate-600">More products coming soon</span>
                  </div>
                </div>
              )}
            </div>

            <Link to="/blog" className={`nav-link ${isActive('/blog') ? 'nav-link-active' : ''}`} data-testid="nav-link-blog">Blog</Link>
            <Link to="/contact" className={`nav-link ${isActive('/contact') ? 'nav-link-active' : ''}`} data-testid="nav-link-contact">Contact</Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {loggedIn ? (
              <Link to="/nurse-dashboard">
                <Button className="btn-primary flex items-center gap-2" data-testid="navbar-dashboard-btn">
                  <LayoutDashboard size={16} /> Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/auth">
                  <Button variant="ghost" className="text-slate-300 hover:text-white flex items-center gap-2" data-testid="navbar-login-btn">
                    <LogIn size={16} /> Login
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-primary" data-testid="navbar-cta">Get in Touch</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="md:hidden p-2 text-slate-400 hover:text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-800/50 animate-fade-up" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.slice(0, 2).map((link) => (
                <Link key={link.path} to={link.path}
                  className={`py-2 text-slate-400 hover:text-white font-medium transition-colors ${isActive(link.path) ? 'text-white' : ''}`}
                  onClick={() => setIsMenuOpen(false)}>
                  {link.name}
                </Link>
              ))}

              {/* Mobile Products Accordion */}
              <button onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                className={`py-2 text-left font-medium transition-colors flex items-center justify-between ${isProductActive ? 'text-white' : 'text-slate-400 hover:text-white'}`}
                data-testid="mobile-products-toggle">
                Products <ChevronDown size={14} className={`transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileProductsOpen && (
                <div className="pl-4 flex flex-col gap-1">
                  {productItems.map((item) => (
                    <Link key={item.path} to={item.path}
                      className="py-2 text-slate-500 hover:text-white text-sm transition-colors"
                      onClick={() => { setIsMenuOpen(false); setMobileProductsOpen(false); }}>
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}

              {navLinks.slice(2).map((link) => (
                <Link key={link.path} to={link.path}
                  className={`py-2 text-slate-400 hover:text-white font-medium transition-colors ${isActive(link.path) ? 'text-white' : ''}`}
                  onClick={() => setIsMenuOpen(false)}>
                  {link.name}
                </Link>
              ))}

              {loggedIn ? (
                <Link to="/nurse-dashboard" onClick={() => setIsMenuOpen(false)}>
                  <Button className="btn-primary w-full mt-2 flex items-center justify-center gap-2" data-testid="mobile-dashboard-btn">
                    <LayoutDashboard size={16} /> Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" className="w-full text-slate-300 hover:text-white mt-2 flex items-center justify-center gap-2" data-testid="mobile-login-btn">
                      <LogIn size={16} /> Login
                    </Button>
                  </Link>
                  <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                    <Button className="btn-primary w-full mt-2" data-testid="mobile-navbar-cta">Get in Touch</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
